// internal/CommandCenterPage.jsx — AYA Command Center (CEO) : KPI réels + AYA recommande + workspaces + responsabilités.
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import InternalLayout, { S, IT } from "@/internal/InternalLayout";
import { CommandBar } from "@/internal/CommandBar";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api/internal`;
const LEVEL = { red: "🔴", orange: "🟠", yellow: "🟡" };

export default function CommandCenterPage() {
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    const t = localStorage.getItem("civ_user_token");
    // Collaborateur → redirection vers SON workspace (RBAC).
    fetch(`${API}/me`, { headers: { Authorization: `Bearer ${t}` } })
      .then((r) => r.json())
      .then((me) => {
        if (me.ok && !me.ceo && me.workspace) { navigate(`/internal/ws/${me.workspace.slug}`, { replace: true }); return; }
        return fetch(`${API}/overview`, { headers: { Authorization: `Bearer ${t}` } })
          .then((r) => r.json()).then((x) => (x.ok ? setD(x) : setErr(true)));
      })
      .catch(() => setErr(true));
  }, [navigate]);

  return (
    <InternalLayout title="AYA Command Center" subtitle="Piloter · Construire · Contrôler · Décider">
      {err && <div style={{ ...S.card, padding: 18, color: "#F87171", fontSize: 13 }} data-testid="cc-error">Accès refusé ou service indisponible.</div>}
      {!d && !err && <div style={{ color: IT.muted, fontSize: 13 }}>Chargement…</div>}
      {d && (
        <>
          <CommandBar onActionDone={() => window.location.reload()} />
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10 }} data-testid="cc-kpis">
            {[
              ["Validations Pro en attente", d.kpis.pending_pro, d.kpis.pending_pro > 0 ? "#F59E0B" : IT.green],
              ["Inscriptions Pro totales", d.kpis.total_pro, IT.text],
              ["Utilisateurs AYA", d.kpis.users, IT.text],
              ["Workspaces", d.kpis.workspaces, IT.text],
              ["Responsabilités", d.kpis.responsibilities, IT.text],
              ["Sans backup", d.kpis.no_backup, d.kpis.no_backup > 0 ? "#F87171" : IT.green],
            ].map(([l, v, c]) => (
              <div key={l} style={{ ...S.card, padding: "12px 14px" }}>
                <div style={{ fontSize: 10, fontWeight: 800, color: IT.muted, textTransform: "uppercase", letterSpacing: 0.5 }}>{l}</div>
                <div style={{ fontSize: 24, fontWeight: 900, color: c, marginTop: 4 }}>{v}</div>
              </div>
            ))}
          </div>

          <div style={{ ...S.card, padding: 16, marginTop: 14 }} data-testid="cc-recommendations">
            <h2 style={{ ...S.h2, color: IT.green, marginBottom: 10 }}>✨ AYA recommande</h2>
            {d.recommendations.map((r, i) => (
              <div key={i} onClick={() => r.action && navigate(r.action)} role={r.action ? "button" : undefined}
                style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 10px", borderRadius: 10, marginBottom: 4, background: "rgba(255,255,255,0.03)", cursor: r.action ? "pointer" : "default", fontSize: 13 }}>
                <span>{LEVEL[r.level] || "🟡"}</span>
                <span style={{ flex: 1 }}>{r.text}</span>
                {r.action && <span style={{ color: IT.green, fontSize: 11.5, fontWeight: 800 }}>Ouvrir →</span>}
              </div>
            ))}
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 12, marginTop: 14 }}>
            <div style={{ ...S.card, padding: 16 }} data-testid="cc-workspaces">
              <h2 style={{ ...S.h2, marginBottom: 10, cursor: "pointer" }} onClick={() => navigate("/internal/workspaces")} data-testid="cc-workspaces-link">👥 Workspaces <span style={{ color: IT.green, fontSize: 11 }}>Tout voir →</span></h2>
              {d.workspaces.map((w) => (
                <div key={w.slug} onClick={() => !w.is_ceo && navigate(`/internal/ws/${w.slug}`)} data-testid={`cc-ws-${w.slug}`}
                  style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: "1px solid rgba(255,255,255,0.05)", cursor: w.is_ceo ? "default" : "pointer" }}>
                  <span style={{ width: 32, height: 32, borderRadius: 9, background: `${w.color}22`, border: `1px solid ${w.color}55`, color: w.color, fontWeight: 900, fontSize: 12, display: "flex", alignItems: "center", justifyContent: "center" }}>{w.owner_name[0]}</span>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: "block", fontSize: 12.5, fontWeight: 800 }}>{w.name}</span>
                    <span style={{ fontSize: 10.5, color: IT.muted }}>{w.owner_name} · {w.role_title}</span>
                  </span>
                  <span style={{ fontSize: 9, fontWeight: 900, color: w.is_ceo ? IT.gold : IT.green, border: `1px solid ${w.is_ceo ? "rgba(232,184,75,0.4)" : "rgba(46,226,123,0.35)"}`, borderRadius: 999, padding: "2px 8px" }}>{w.is_ceo ? "CEO" : "Ouvrir →"}</span>
                </div>
              ))}
            </div>
            <div style={{ ...S.card, padding: 16 }} data-testid="cc-responsibilities">
              <h2 style={{ ...S.h2, marginBottom: 10 }}>🛡 Responsabilités</h2>
              {d.responsibilities.map((r) => (
                <div key={r.key} style={{ padding: "8px 0", borderBottom: "1px solid rgba(255,255,255,0.05)", fontSize: 12 }}>
                  <div style={{ fontWeight: 800, fontSize: 12.5 }}>{r.label}</div>
                  <div style={{ color: IT.muted, marginTop: 2 }}>
                    Resp. <strong style={{ color: IT.text }}>{r.owner}</strong> · Backup <strong style={{ color: r.backup ? IT.text : "#F87171" }}>{r.backup || "—"}</strong> · Agent <span style={{ color: IT.green }}>{r.agent}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div style={{ ...S.card, padding: 16, marginTop: 14 }} data-testid="cc-audit">
            <h2 style={{ ...S.h2, marginBottom: 10 }}>📜 Activité récente (audit)</h2>
            {d.audit_recent.length === 0 && <div style={{ fontSize: 12, color: IT.muted }}>Aucune action enregistrée.</div>}
            {d.audit_recent.map((a, i) => (
              <div key={i} style={{ display: "flex", gap: 10, fontSize: 11.5, padding: "5px 0", color: IT.muted }}>
                <span style={{ color: IT.green, fontWeight: 700 }}>{a.action}</span>
                <span style={{ flex: 1 }}>{a.user}</span>
                <span>{new Date(a.ts).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </InternalLayout>
  );
}
